import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { getCustomerNotes } from "@/lib/demo-data/customer-notes";
import type { CustomerNote, CustomerNoteApiItem, CustomerNotesApiResponse, CreateNoteRequest } from "@/types/customer-note";
import { useMutation, useQueryClient } from "@tanstack/react-query";

function mapApiItemToCustomerNote(item: CustomerNoteApiItem): CustomerNote {
  return {
    id: String(item.id),
    customerId: String(item.customerId),
    content: item.content,
    createdAt: item.createdAt,
    createdBy: {
      id: String(item.createdByUserId),
      name: item.createdByUserName,
    },
  };
}

/**
 * Hook to fetch customer notes
 * Handles both Demo and Live modes
 */
export function useCustomerNotes(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockNotes = customerId ? getCustomerNotes(customerId) : [];

  return useModeAwareQuery<CustomerNote[], Error>(
    ["customer-notes", customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<CustomerNotesApiResponse>(
        `/api/v1/customers/${customerId}/notes`,
        undefined,
        signal
      );
      return (response.notes || []).map(mapApiItemToCustomerNote);
    },
    mockNotes,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to add a new customer note
 * Handles both Demo and Live modes
 */
export function useAddCustomerNote() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateNoteRequest & { userId: string; userName: string }) => {
      const response = await apiPost<CustomerNoteApiItem>(
        `/api/v1/customers/${request.customerId}/notes`,
        {
          content: request.content,
        }
      );
      return mapApiItemToCustomerNote(response);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["customer-notes", variables.customerId] });
    },
  });
}
